import { AxiosError } from 'axios';
import { useMutation, useQueryClient } from 'react-query';
import { Axios } from '@utils/api/axios-instance.ts';
import { VOICE_IDENTIFICATION_URL } from '@utils/api/endpoints.voice-profiles.ts';
import { BaseError } from '@utils/types/shared/base.ts';
import {
  VoiceSamplePlan,
  VoiceSamplePreview,
  VoiceSampleRange
} from '@utils/types/organization/voice-profile.ts';
import { VOICE_PROFILE_QUERY_KEYS } from '@utils/api/organizations/speakers/voice-profiles/query-keys.ts';
import { voiceErrorKey } from '@utils/editors/voice-profile-errors.ts';
import { totalDurationMs } from '@utils/editors/voice-sample-ranges.ts';

/** Ask the backend's clean-speech planner for an initial selection.
 *
 * This reuses the same planner the batch worker uses, so the preselection
 * already excludes overlapping and unattributed speech — the user adjusts a
 * sensible starting point instead of hunting for clean audio by ear.
 * Insufficient clean speech answers 400 and an unknown label 404; both are
 * explained in the dialog. */
export const useVoiceSamplePlan = (
  sessionId: string | undefined,
  documentId: string | undefined
) => {
  const queryClient = useQueryClient();
  return useMutation<
    VoiceSamplePlan,
    AxiosError<BaseError>,
    { speakerLabel: string }
  >(
    ({ speakerLabel }) =>
      // StrictMode and separate controls can ask for the same plan together.
      // Share that request so they cannot compete for the backend's CPU slot.
      queryClient.fetchQuery<VoiceSamplePlan, AxiosError<BaseError>>(
        VOICE_PROFILE_QUERY_KEYS.samplePlan(
          sessionId ?? '',
          documentId ?? '',
          speakerLabel
        ),
        async () => {
          const { data } = await Axios.post<VoiceSamplePlan>(
            VOICE_IDENTIFICATION_URL.samplePlan(
              sessionId as string,
              documentId as string
            ),
            { speaker_label: speakerLabel }
          );
          return data;
        },
        {
          staleTime: 0,
          cacheTime: 0,
          // Another document can legitimately own the shared extraction slot.
          // Retry this read briefly; enrollment writes are never retried here.
          retry: (failures, error) =>
            failures < 2 && voiceErrorKey(error) === 'voice-error-busy',
          retryDelay: attempt => 500 * 2 ** attempt
        }
      ),
    {
      onError: () => {
        /* Explained in the dialog after bounded busy retries. */
      },
      useErrorBoundary: false
    }
  );
};

/** Cut the chosen ranges server-side and return a disposable preview WAV.
 *
 * The response is already mono 16 kHz PCM16 — exactly what the enrollment
 * route wants — so these bytes are kept in memory and submitted verbatim. The
 * browser never downloads the full recording and never encodes audio, and the
 * bytes are never written to storage: `responseType: 'arraybuffer'` keeps them
 * off the response-text path, and the server sends `no-store`.
 *
 * The `X-Voice-Sample-SHA256` header identifies the exact preview, which lets
 * the dialog prove the bytes it enrolls are the bytes the admin heard. */
export const useVoiceSamplePreview = (
  sessionId: string | undefined,
  documentId: string | undefined
) =>
  useMutation<
    VoiceSamplePreview,
    AxiosError<BaseError>,
    { ranges: VoiceSampleRange[] }
  >(
    async ({ ranges }) => {
      const response = await Axios.post<ArrayBuffer>(
        VOICE_IDENTIFICATION_URL.samplePreview(
          sessionId as string,
          documentId as string
        ),
        { ranges },
        { responseType: 'arraybuffer' }
      );
      const sha =
        (response.headers?.['x-voice-sample-sha256'] as string | undefined) ??
        null;
      return {
        bytes: response.data,
        sha256: sha,
        durationMs: totalDurationMs(ranges),
        ranges
      };
    },
    {
      onError: () => {
        /* Explained in the dialog. */
      },
      useErrorBoundary: false
    }
  );
